import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  SectionLabel,
} from '@wundo/ui';
import type { OutreachQueueEntry } from '@/lib/outreachQueue.ts';
import {
  formatFireTime,
  kindLabel,
  outreachUserName,
  relativeFromNow,
} from './outreachFormat.ts';

export interface OutreachDetailDialogProps {
  /** The row being looked at; null closes the dialog. */
  entry: OutreachQueueEntry | null;
  onClose: () => void;
}

/**
 * Everything the queue holds about one waiting task.
 *
 * The table is a scan of what is coming; this is the row opened up — the exact
 * instants, the user's notification hour, and the payload the job will send.
 */
export function OutreachDetailDialog({ entry, onClose }: OutreachDetailDialogProps) {
  return (
    <Dialog
      open={entry !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent size='lg'>
        {entry && (
          <>
            <DialogHeader>
              <DialogTitle>
                {kindLabel(entry.kind)} — {entry.skill?.name ?? 'Unknown skill'}
              </DialogTitle>
            </DialogHeader>

            <div className='flex flex-col gap-4 px-5 py-4'>
              <dl className='grid grid-cols-2 gap-x-6 gap-y-3 text-sm max-mobile:grid-cols-1'>
                <Detail label='Fires at (user time)'>
                  <p className='font-semibold text-ink'>
                    {formatFireTime(entry.fire_at, entry.user.timezone)}
                  </p>
                  <p className='text-xs text-ink-muted'>
                    {relativeFromNow(entry.fire_at)} · {entry.user.timezone}
                  </p>
                </Detail>
                <Detail label='Expires'>
                  <p className='font-semibold text-ink'>
                    {formatFireTime(entry.expire_at, entry.user.timezone)}
                  </p>
                </Detail>
                <Detail label='Notification time'>
                  <p className='font-mono text-ink'>{entry.user.notification_time}</p>
                </Detail>
                <Detail label='Earliest fire (not_before)'>
                  <p className='text-xs text-ink-muted'>
                    {formatFireTime(entry.not_before, entry.user.timezone)}
                  </p>
                </Detail>
                <Detail label='User'>
                  <Link
                    to='/users/$id'
                    params={{ id: entry.user.id }}
                    hash='skills'
                    className='font-semibold text-ink hover:underline'
                  >
                    {outreachUserName(entry.user)}
                  </Link>
                </Detail>
                <Detail label='Organisation'>
                  <p className='text-ink'>{entry.organisation.name}</p>
                </Detail>
                <Detail label='Priority'>
                  <p className='font-mono text-ink'>{entry.priority}</p>
                </Detail>
                <Detail label='Task ID'>
                  <p className='font-mono text-xs break-all text-ink'>{entry.task_id}</p>
                </Detail>
              </dl>

              <section className='border-t border-line pt-3'>
                <SectionLabel as='h3' className='mb-2'>
                  Payload
                </SectionLabel>
                <Payload entry={entry} />
              </section>
            </div>

            <DialogFooter>
              <Button variant='quiet' onClick={onClose}>
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className='text-xs text-ink-muted'>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Payload({ entry }: { entry: OutreachQueueEntry }) {
  if (entry.kind === 'recommendation') {
    return (
      <div className='flex flex-col gap-2 text-sm'>
        <div>
          <p className='text-xs text-ink-muted'>Status</p>
          <p className='text-ink'>{entry.payload.recommendation_status ?? '—'}</p>
        </div>
        {entry.payload.recommendation_score !== null && (
          <div>
            <p className='text-xs text-ink-muted'>Final score</p>
            <p className='font-mono text-ink'>
              {entry.payload.recommendation_score.toFixed(1)}
            </p>
          </div>
        )}
        <div>
          <p className='text-xs text-ink-muted'>Reason</p>
          <p className='text-ink-secondary'>
            {entry.payload.recommendation_reason ?? '—'}
          </p>
        </div>
      </div>
    );
  }

  if (entry.kind === 'reminder') {
    return (
      <div className='text-sm'>
        <p className='mb-1 text-xs text-ink-muted'>Reminder content</p>
        <p className='text-ink-secondary'>{entry.payload.reminder_content ?? '—'}</p>
      </div>
    );
  }

  return (
    <div className='text-sm'>
      <p className='mb-1 text-xs text-ink-muted'>Cadence</p>
      <p className='text-ink-secondary'>{entry.payload.rrule_human ?? '—'}</p>
      {entry.payload.rrule && (
        <p className='mt-1 font-mono text-xs text-ink-muted'>{entry.payload.rrule}</p>
      )}
    </div>
  );
}
